// Shopify Phase 6 (2026-10-15) — operational health view.
//
// Super-admin only. Lists every brand with an active shopify_app
// credential and surfaces liveness signals that matter for a first-
// client rollout:
//   - silent for >7 days (no webhooks → likely subscription issue
//     or merchant disabled the app on their end)
//   - scope mismatch (Partner Dashboard scope change not yet re-
//     installed by the merchant)
//   - auto-push enabled / disabled per brand
//   - product count + how many have had images auto-pushed back
//
// Pairs with docs/SHOPIFY_FIRST_CLIENT_RUNBOOK.md — the runbook
// references each column by name as a diagnostic checklist.

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, Code, HStack, Link,
  Skeleton, Text, VStack,
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useAuth } from '../../auth/AuthContext';

type HealthRow = {
  credId:            string;
  brandId:           string;
  brandName:         string | null;
  shopDomain:        string | null;
  connectedAt:       string | null;
  lastUsedAt:        string | null;
  lastCatalogSyncAt: string | null;
  lastWebhookAt:     string | null;
  lastWebhookTopic:  string | null;
  silentForDays:     number | null;
  silent:            boolean | null;
  productCount:      number;
  pushedImageCount:  number;
  webhookSubCount:   number;
  webhookSubTopics:  string[];
  scopeMatch:        boolean;
  scopeMissing:      string[];
  scopeGranted:      number;
  scopeRequested:    number;
  autoPushEnabled:   boolean;
};

type HealthResponse = {
  ok: boolean;
  summary: {
    totalBrands: number;
    silentBrands: number;
    scopeMismatchBrands: number;
    autoPushEnabledBrands: number;
  };
  silentThresholdDays: number;
  requiredScopes: string[];
  brands: HealthRow[];
};

const REFRESH_MS = 60_000;

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AdminShopifyHealthPage({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const auth = useAuth();
  const jwtIsAdmin = auth.status === 'authenticated' && auth.user.isSuperAdmin === true;

  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(jwtIsAdmin);
  const [forbidden, setForbidden] = useState(!jwtIsAdmin);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!jwtIsAdmin) return;
    if (!opts.silent) setLoading(true);
    try {
      const r = await apiJson<HealthResponse>('/api/admin/shopify-health');
      setData(r);
      setError(null);
      setForbidden(false);
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 403) {
        setForbidden(true);
        setData(null);
      } else {
        setError(err instanceof Error ? err.message : 'load failed');
      }
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [jwtIsAdmin]);

  useEffect(() => {
    load();
    const t = window.setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  if (forbidden) {
    return (
      <Box>
        {!hideHeader && (
          <PageHeader title="Shopify health" description="Operational health view for connected Shopify brands." />
        )}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Restricted to super-admins.
          </Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Shopify health"
          description="Operational view of every brand with an active Shopify credential. Flags silent brands, scope mismatches, and push status."
        />
      )}
      <VStack align="stretch" spacing={4}>
        {data?.summary && (
          <Card variant="outline">
            <CardBody>
              <HStack spacing={6} fontSize="sm" flexWrap="wrap">
                <Text><strong>{data.summary.totalBrands}</strong> connected brands</Text>
                <Text color={data.summary.silentBrands > 0 ? 'red.600' : 'brand.muted'}>
                  <strong>{data.summary.silentBrands}</strong> silent &gt;{data.silentThresholdDays}d
                </Text>
                <Text color={data.summary.scopeMismatchBrands > 0 ? 'orange.600' : 'brand.muted'}>
                  <strong>{data.summary.scopeMismatchBrands}</strong> scope mismatch
                </Text>
                <Text>
                  <strong>{data.summary.autoPushEnabledBrands}</strong> auto-push on
                </Text>
                <Text color="brand.muted" fontSize="xs">
                  {data.requiredScopes.length} required scopes
                </Text>
                <Button size="xs" variant="ghost" onClick={() => load()} isLoading={loading}>
                  Refresh
                </Button>
              </HStack>
            </CardBody>
          </Card>
        )}

        {loading && !data && (
          <VStack align="stretch" spacing={2}>
            <Skeleton height="80px" />
            <Skeleton height="80px" />
          </VStack>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        )}

        {data && data.brands.length === 0 && (
          <Card variant="outline">
            <CardBody>
              <Text fontSize="sm" color="brand.muted" textAlign="center">
                No brands have an active Shopify credential.
              </Text>
            </CardBody>
          </Card>
        )}

        {data && data.brands.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {data.brands.map((b) => (
              <Card
                key={b.credId}
                variant="outline"
                borderColor={b.silent ? 'red.300' : !b.scopeMatch ? 'orange.300' : 'brand.border'}
                bg={b.silent ? 'red.50' : !b.scopeMatch ? 'orange.50' : 'white'}
              >
                <CardBody>
                  <HStack justify="space-between" align="flex-start" spacing={3} flexWrap="wrap">
                    <VStack align="flex-start" spacing={1} flex="1" minW="200px">
                      <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="600">
                          {b.brandName || `brand ${b.brandId.slice(-6)}`}
                        </Text>
                        {b.autoPushEnabled && <Badge colorScheme="purple" fontSize="9px">auto-push</Badge>}
                        {b.silent && <Badge colorScheme="red" fontSize="9px">silent {b.silentForDays}d</Badge>}
                        {!b.scopeMatch && <Badge colorScheme="orange" fontSize="9px">scope mismatch</Badge>}
                      </HStack>
                      {b.shopDomain && (
                        <HStack spacing={1}>
                          <Code fontSize="xs">{b.shopDomain}</Code>
                          <Link
                            href={`https://admin.shopify.com/store/${b.shopDomain.replace(/\.myshopify\.com$/, '')}`}
                            isExternal
                            fontSize="xs"
                            color="purple.600"
                          >
                            open ↗
                          </Link>
                        </HStack>
                      )}
                    </VStack>
                    <VStack align="flex-end" spacing={0.5} fontSize="xs" color="brand.muted" minW="180px">
                      <Text>{b.productCount} products · {b.pushedImageCount} with pushed images</Text>
                      <Text>{b.webhookSubCount}/4 webhook subs · scopes {b.scopeGranted}/{b.scopeRequested}</Text>
                      <Text>connected {fmtDate(b.connectedAt)}</Text>
                      {b.lastWebhookAt && (
                        <Text>
                          last webhook {fmtDate(b.lastWebhookAt)}
                          {b.lastWebhookTopic && <> ({b.lastWebhookTopic})</>}
                        </Text>
                      )}
                      {b.lastCatalogSyncAt && (
                        <Text>last catalog sync {fmtDate(b.lastCatalogSyncAt)}</Text>
                      )}
                    </VStack>
                  </HStack>
                  {!b.scopeMatch && b.scopeMissing.length > 0 && (
                    <Alert status="warning" mt={2} borderRadius="sm" py={1.5}>
                      <AlertIcon />
                      <Text fontSize="11px">
                        Merchant granted {b.scopeGranted} scopes, but {b.scopeMissing.length} required scopes are missing:
                        {' '}
                        <Code fontSize="10px">{b.scopeMissing.join(', ')}</Code>.
                        Merchant must re-install the app to grant the full set.
                      </Text>
                    </Alert>
                  )}
                  {b.silent && (
                    <Alert status="error" mt={2} borderRadius="sm" py={1.5}>
                      <AlertIcon />
                      <Text fontSize="11px">
                        No webhook received in {b.silentForDays}+ days. Likely causes: merchant disabled the app on their end (no app/uninstalled webhook arrived), webhook subscription expired at Shopify, or no catalog changes have occurred (benign if the brand genuinely has a frozen catalog).
                      </Text>
                    </Alert>
                  )}
                </CardBody>
              </Card>
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

export default AdminShopifyHealthPage;
